// Fabric setup - a numbered checklist that mirrors the fabric-probe steps.
// G360_K634
//
// Each step shows the state the last check recorded (ok / missing /
// unknown / not applicable), what to do in plain words, and the commands to
// paste. Nothing here changes Microsoft: the commands are for a Microsoft
// administrator to run. "Check again" asks fabric-probe to re-test every
// step; "Sync now" reads Fabric once (owner/admin). The optional preview feed
// (capacity operation events in the customer's own Eventhouse) is saved with
// fabric_set_settings.
//
// Props: open, onClose, tenantId, view (fabric_view result, optional - read
// here when absent), onChanged (called after a check, a sync or a save),
// focus ('script' scrolls to the one script on open - G360_K636).
//
// G360_K636 - the multi-step copy-paste list became ONE generated PowerShell
// script for Azure Cloud Shell (lib/fabricSetupScript.js, the same script the
// Microsoft connection setup appends for new customers). The checklist keeps
// the state of each step; the old commands are under "Do it by hand instead".
//
// G360_K637 - zero steps for organisations with the Azure connection. The
// drawer opens with ONE line saying whether Fabric data works (fabric_view
// .status, 0331): through the Azure connection, through the Govern360 app,
// or not connected yet. Not connected, with an Azure connection: Check again
// and Sync now (the daily sync may not have run). Not connected, without
// one: Connect Azure (opens the existing Azure connection wizard; its script
// assigns the Reader role, which lists Fabric capacities and reads their
// cost). The kit 636 script moved under "Optional: add AI items"; the
// capacity role is an optional extra, not a required step.
import { useEffect, useRef, useState } from 'react';
import { Copy, Check, RefreshCw, ExternalLink, Download } from 'lucide-react';
import Drawer from '@govern360/shared/components/Drawer.jsx';
import { useAuth } from '@govern360/shared';
import { RankHead } from './controlKit.jsx';
import { readFabricView, runFabricProbe, runFabricSync, setFabricSettings, readAppIds, readAzureAppId } from '../lib/fabricApi.js';
// G360_K637
import { fabricStatus, fabricModeLine, isWorking, CONNECT_AZURE_HASH } from '../lib/fabricApi.js';
// G360_K636
import { generateFabricSetupPs1, FABRIC_GROUP_NAME, FABRIC_SCRIPT_FILE, safeGuid } from '../lib/fabricSetupScript.js';

export const STEP_ORDER = ['microsoft_connected', 'fabric_token', 'sp_public_api', 'capacity_role', 'admin_api', 'azure_capacities', 'azure_cost', 'events'];
// G360_K637 - either path is enough; the rest are optional extras
export const PATHS = [
  { key: 'azure', title: 'Through your Azure connection', sub: 'Nothing to set up in Fabric.', steps: ['azure_capacities', 'azure_cost'] },
  { key: 'fabric', title: 'Or through the Govern360 app', sub: 'Needs Fabric to let the app call its APIs.', steps: ['microsoft_connected', 'fabric_token', 'sp_public_api'] },
];
export const EXTRA_STEPS = ['admin_api', 'capacity_role', 'events'];
// numbered in the order they are shown
const SHOWN_ORDER = PATHS.reduce((a, x) => a.concat(x.steps), []).concat(EXTRA_STEPS);
const STEP_TITLE = {
  microsoft_connected: 'Microsoft is connected to Govern360',
  fabric_token: 'The Govern360 app can sign in to Fabric',
  sp_public_api: 'Fabric lets the app call its public APIs',
  capacity_role: 'The app has a role on each capacity',
  admin_api: 'Fabric lets the app read the admin inventory',
  azure_capacities: 'Your Azure connection lists the Fabric capacities',
  azure_cost: 'Azure cost for the Fabric capacities (dollars)',
  events: 'Capacity operation events (optional, Preview)',
};
const STATE_STYLE = {
  ok: { label: 'OK', color: '#188038', bg: '#e6f4ea' },
  missing: { label: 'Missing', color: '#b31412', bg: '#fce8e6' },
  unknown: { label: 'Not checked', color: '#5f6368', bg: 'var(--g-bg-soft)' },
  not_applicable: { label: 'Not applicable', color: '#5f6368', bg: 'var(--g-bg-soft)' },
};
export const stepState = (s) => STATE_STYLE[(s && s.state) || 'unknown'] || STATE_STYLE.unknown;
// G360_K636 - one group name for the script and the manual steps
const GROUP = FABRIC_GROUP_NAME;
// Steps done by hand (the rest - Microsoft connected, the optional events
// feed - keep their own panel in the checklist).
const MANUAL_KEYS = ['fabric_token', 'sp_public_api', 'capacity_role', 'admin_api', 'azure_cost'];

function Snip({ text, testid, maxHeight }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ position: 'relative', margin: '6px 0' }}>
      {/* G360_K636 maxHeight: the one script scrolls inside its box */}
      <pre data-testid={testid} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 11, background: 'var(--g-surface)', border: '1px solid var(--g-divider)', padding: '8px 64px 8px 8px', borderRadius: 6, margin: 0, ...(maxHeight ? { maxHeight, overflow: 'auto' } : {}) }}>{text}</pre>
      <button type="button" className="btn outline" data-testid={testid ? testid + '-copy' : undefined} style={{ position: 'absolute', top: 4, right: 4, height: 24, fontSize: 11, padding: '0 8px' }}
        onClick={async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { setCopied(false); } }}>
        {copied ? <Check size={12} /> : <Copy size={12} />}{copied ? ' Copied' : ' Copy'}
      </button>
    </div>
  );
}

function P({ children }) { return <div style={{ fontSize: 12.5, color: 'var(--g-text-2)', lineHeight: 1.6, marginTop: 4 }}>{children}</div>; }

export default function FabricSetupDrawer({ open, onClose, tenantId, view, onChanged, focus }) {
  const { tenant, activeRole } = useAuth();
  const tid = tenantId || (tenant && tenant.id) || null;
  const [own, setOwn] = useState(null);
  const [ids, setIds] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ uri: '', db: '', table: '', enabled: false });
  // G360_K636 - the Azure connection's app id for step 4 of the script
  const [azId, setAzId] = useState(null);
  // G360_K637 - whether the organisation has an Azure connection at all
  const [azExists, setAzExists] = useState(null);
  const extrasRef = useRef(null);
  const scriptRef = useRef(null);
  const v = view || (own && own.data) || null;
  const missing = !view && own && own.missing;
  const canManage = !!(v && v.can_manage) || activeRole === 'owner' || activeRole === 'admin';

  async function loadOwn() { if (!tid) return; setOwn(await readFabricView(tid, 30)); }
  useEffect(() => {
    if (!open || !tid) return;
    setMsg(null);
    if (!view) loadOwn();
    let live = true;
    readAppIds(tid).then((r) => { if (live) setIds(r); });
    // G360_K636
    readAzureAppId(tid).then((r) => { if (live) { setAzId((r && r.clientId) || null); setAzExists(r ? r.exists : null); } });
    return () => { live = false; };
  }, [open, tid]); // eslint-disable-line react-hooks/exhaustive-deps
  // G360_K636 - focus 'script' goes straight to the script
  // G360_K637 - focus 'extras' goes to the optional AI items section
  useEffect(() => {
    if (!open || (focus !== 'script' && focus !== 'extras')) return undefined;
    const t = setTimeout(() => { const el = focus === 'extras' ? extrasRef.current : scriptRef.current; if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' }); }, 60);
    return () => clearTimeout(t);
  }, [open, focus]);
  const settings = (v && v.settings) || null;
  useEffect(() => {
    if (!open) return;
    setForm({ uri: (settings && settings.eventhouse_uri) || '', db: (settings && settings.eventhouse_db) || '', table: (settings && settings.eventhouse_table) || '', enabled: !!(settings && settings.events_enabled) });
  }, [open, settings && settings.eventhouse_uri, settings && settings.eventhouse_db, settings && settings.eventhouse_table, settings && settings.events_enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const probe = (v && v.probe) || null;
  const steps = {}; ((probe && probe.steps) || []).forEach((s) => { if (s && s.key) steps[s.key] = s; });
  const okCount = STEP_ORDER.filter((k) => steps[k] && steps[k].state === 'ok').length;
  const checked = !!(probe && probe.checked_at);
  const cid = (ids && ids.clientId) || '<govern360-app-client-id>';
  const etid = (ids && ids.entraTenantId) || '<your-entra-tenant-id>';
  // G360_K636 - the one script, with this organisation's identifiers filled in
  // (identifiers only; anything missing is asked for by the script).
  const script = generateFabricSetupPs1({ clientId: ids && ids.clientId, entraTenantId: ids && ids.entraTenantId, azureAppId: azId });
  const scriptHasIds = !!(safeGuid(ids && ids.clientId) && safeGuid(ids && ids.entraTenantId));
  function downloadScript() {
    try {
      const blob = new Blob([script], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = FABRIC_SCRIPT_FILE; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* copy is still available */ }
  }

  async function after() { if (!view) await loadOwn(); if (onChanged) onChanged(); }
  async function check() {
    setBusy('check'); setMsg(null);
    const r = await runFabricProbe(tid);
    setMsg(r.ok ? { ok: true, text: 'Checked. Each step below shows what Microsoft answered.' } : { ok: false, text: r.error });
    await after(); setBusy(null);
  }
  async function sync() {
    setBusy('sync'); setMsg(null);
    const r = await runFabricSync(tid);
    setMsg(r.ok ? { ok: true, text: 'Read from Fabric. The figures on the Fabric tab are updated.' } : { ok: false, text: r.error });
    await after(); setBusy(null);
  }
  async function save() {
    setBusy('save'); setMsg(null);
    const r = await setFabricSettings(tid, form.uri.trim(), form.db.trim(), form.table.trim(), form.enabled);
    setMsg(r.ok ? { ok: true, text: form.enabled ? 'Saved. The next read queries your Eventhouse.' : 'Saved. The preview feed is off.' } : { ok: false, text: r.error });
    if (r.ok) await after();
    setBusy(null);
  }

  // G360_K637 - one status for the whole drawer
  const st = fabricStatus(v);
  const working = isWorking(st.mode);
  const basis = (v && v.connected_basis) || {};
  const hasAzure = azExists === true || basis.azure_cost === 'connection';
  const headColor = !v ? '#5f6368' : working ? '#188038' : checked ? '#e8710a' : '#1a73e8';
  const body = (key) => {
    if (key === 'microsoft_connected') return (<>
      <P>Govern360 uses the Microsoft (Entra) app your organisation already connected. No new connection is needed. If Microsoft is not connected, connect it on the Connections page first.</P>
      <button type="button" className="btn outline" data-testid="fb-setup-connections" style={{ height: 26, fontSize: 12, marginTop: 6 }} onClick={() => { if (onClose) onClose(); window.location.hash = '#connections'; }}>Open Connections<ExternalLink size={12} /></button>
    </>);
    if (key === 'fabric_token') return (<>
      <P>Govern360 asks Microsoft for a read-only Fabric token with the same app and credential as your Microsoft connection. If this fails, the app's secret or certificate on the Microsoft connection is not valid, or Fabric is not enabled in your Microsoft tenant.</P>
      <P>Govern360 app (client) ID: <span className="mono" data-testid="fb-setup-cid">{ids && ids.clientId ? ids.clientId : 'shown once Microsoft is connected'}</span></P>
    </>);
    if (key === 'sp_public_api') return (<>
      <P>1. Put the Govern360 app in a security group. In PowerShell (Microsoft Graph):</P>
      <Snip testid="fb-snip-group-ps" text={[
        "Connect-MgGraph -Scopes 'Group.ReadWrite.All','Application.Read.All'",
        "$sp = Get-MgServicePrincipal -Filter \"appId eq '" + cid + "'\"",
        "$g  = New-MgGroup -DisplayName '" + GROUP + "' -MailEnabled:$false -MailNickname 'govern360-fabric' -SecurityEnabled",
        'New-MgGroupMember -GroupId $g.Id -DirectoryObjectId $sp.Id',
      ].join('\n')} />
      <P>or with the Azure CLI:</P>
      <Snip testid="fb-snip-group-az" text={[
        'az ad group create --display-name "' + GROUP + '" --mail-nickname govern360-fabric',
        'az ad group member add --group "' + GROUP + '" --member-id $(az ad sp show --id ' + cid + ' --query id -o tsv)',
      ].join('\n')} />
      <P>2. In the Fabric admin portal: Tenant settings, Developer settings, enable <strong>Service principals can call Fabric public APIs</strong> and apply it to the security group <strong>{GROUP}</strong>.</P>
    </>);
    if (key === 'capacity_role') return (<>
      <P>Fabric only lists a capacity to an app that is a capacity admin or contributor on it. In the Fabric admin portal: Capacity settings, Fabric capacity, choose each capacity, then Contributor permissions (or Capacity administrators) and add the security group <strong>{GROUP}</strong>. Govern360 reads; it never changes a capacity.</P>
    </>);
    if (key === 'admin_api') return (<>
      <P>In the Fabric admin portal: Tenant settings, Admin API settings, enable <strong>Service principals can access read-only admin APIs</strong> for the security group <strong>{GROUP}</strong>. This lets Govern360 list the workspaces and the AI items (data agents, ontologies, operations agents) on each capacity. Microsoft allows 200 of these calls an hour; Govern360 stays well inside it.</P>
    </>);
    if (key === 'azure_capacities') return (<>
      <P>Govern360 lists the Fabric capacities (Microsoft.Fabric/capacities) in each subscription of your Azure connection. The Reader role that the Azure connection's setup script assigns is enough. Nothing is set up in Fabric.</P>
    </>);
    if (key === 'azure_cost') return (<>
      <P>Dollars come from Azure Cost Management, per Fabric capacity and meter, through your Azure connection on the Connections page. The Reader role that connection's setup script assigns can read cost. If your organisation gave the connection's app a narrower role, give it Cost Management Reader on the subscription that holds the Fabric capacities:</P>
      {/* G360_K636 - the Azure connection's app when it is known */}
      <Snip testid="fb-snip-cost" text={'az role assignment create --role "Cost Management Reader" --assignee ' + (azId || cid) + ' --scope /subscriptions/<subscription-id>'} />
      <P>If your Azure connection uses a different app registration, put that app's ID after --assignee. Without this, Govern360 shows capacity units and marks every dollar figure as an estimate.</P>
    </>);
    if (key === 'events') return (<>
      <P>Optional, and a Microsoft Preview. It is the only Microsoft source that says which person or agent used how many capacity units. In Fabric: Real-Time hub, Fabric events, Capacity operation events, create an Eventstream for each capacity with an Eventhouse as the destination. The stream itself consumes capacity units on your capacity.</P>
      <P>Then let the Govern360 app read that database (run in the Eventhouse query editor):</P>
      <Snip testid="fb-snip-kql" text={".add database ['<database>'] viewers ('aadapp=" + cid + ';' + etid + "') 'Govern360 read-only'"} />
      <P>Then tell Govern360 where to read:</P>
      <div data-testid="fb-setup-events-form" style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 6, alignItems: 'center', marginTop: 6, fontSize: 12 }}>
        <label htmlFor="fb-eh-uri">Query URI</label>
        <input id="fb-eh-uri" className="inp" data-testid="fb-eh-uri" disabled={!canManage} value={form.uri} placeholder="https://<id>.kusto.fabric.microsoft.com" onChange={(e) => setForm({ ...form, uri: e.target.value })} style={{ height: 28 }} />
        <label htmlFor="fb-eh-db">Database</label>
        <input id="fb-eh-db" className="inp" data-testid="fb-eh-db" disabled={!canManage} value={form.db} onChange={(e) => setForm({ ...form, db: e.target.value })} style={{ height: 28 }} />
        <label htmlFor="fb-eh-table">Table</label>
        <input id="fb-eh-table" className="inp" data-testid="fb-eh-table" disabled={!canManage} value={form.table} onChange={(e) => setForm({ ...form, table: e.target.value })} style={{ height: 28 }} />
        <span />
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><input type="checkbox" data-testid="fb-eh-on" disabled={!canManage} checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />Read this feed</label>
      </div>
      {canManage
        ? <button type="button" className="btn primary" data-testid="fb-eh-save" style={{ height: 28, fontSize: 12, marginTop: 8 }} disabled={busy === 'save'} onClick={save}>{busy === 'save' ? 'Saving...' : 'Save'}</button>
        : <P>An owner or admin can set this.</P>}
    </>);
    return null;
  };

  // G360_K637 - one checklist row (state, detail, and its own panel when it has one)
  const stepItem = (k, i) => {
    const s0 = steps[k]; const sst = stepState(s0);
    return (
      <li key={k} data-testid={'fb-step-' + k} data-state={(s0 && s0.state) || 'unknown'} style={{ border: '1px solid var(--g-divider)', borderLeft: '3px solid ' + sst.color, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 22, height: 22, borderRadius: 11, background: 'var(--g-bg-soft)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700 }}>{i + 1}</span>
          <strong style={{ flex: 1, fontSize: 13 }}>{(s0 && s0.label) || STEP_TITLE[k]}</strong>
          {EXTRA_STEPS.indexOf(k) >= 0 && <span className="pill" style={{ background: 'var(--g-bg-soft)', color: 'var(--g-text-2)', fontSize: 10.5 }}>Optional</span>}
          <span className="pill" data-testid={'fb-step-' + k + '-pill'} style={{ background: sst.bg, color: sst.color, fontWeight: 700, fontSize: 10.5 }}>{sst.label}</span>
        </div>
        {s0 && s0.detail && <div data-testid={'fb-step-' + k + '-detail'} style={{ fontSize: 12, marginTop: 4, color: sst.color }}>{s0.detail}</div>}
        {/* G360_K636 - the commands moved to the one script and to "Do it by hand instead" */}
        {MANUAL_KEYS.indexOf(k) === -1 && body(k)}
      </li>
    );
  };

  return (
    <Drawer open={open} title="Set up Fabric" onClose={onClose} width={640} footer={<button className="btn outline" onClick={onClose}>Close</button>}>
      {open && (
        <div data-testid="fb-setup">
          {/* G360_K634 */}
          {/* G360_K637 - the header says whether Fabric data works */}
          <RankHead testid="fb-setup-head" color={headColor} kicker={'Fabric setup \u00b7 ' + (checked ? 'last checked ' + new Date(probe.checked_at).toLocaleString() : 'not checked yet')}
            title="Fabric capacity and AI consumption"
            sub="Read-only. Uses your existing Azure connection or your existing Microsoft app - no new app or connection."
            big={working ? 'Working' : st.mode === 'not_checked' ? 'Not checked' : 'Not connected'}
            bigSub={st.mode === 'working_azure' ? 'through Azure' : st.mode === 'working_fabric' ? 'through the Govern360 app' : st.mode === 'working_both' ? 'Azure and the app' : 'run Check again'} />
          {missing && <div className="banner warn" data-testid="fb-setup-notyet" style={{ marginTop: 10, fontSize: 12.5 }}>Fabric is not switched on for this organisation yet. The steps below can be done now; Govern360 checks them once it is.</div>}
          {/* G360_K637 - ONE summary line and, when not working, one clear next step */}
          <section data-testid="fb-setup-summary" data-mode={st.mode} style={{ border: '1px solid var(--g-divider)', borderLeft: '3px solid ' + (working ? '#188038' : '#e8710a'), borderRadius: 8, padding: '10px 12px', marginTop: 10 }}>
            <strong data-testid="fb-setup-summary-line" style={{ fontSize: 14, color: working ? '#188038' : 'var(--g-text)' }}>{fabricModeLine(st.mode)}</strong>
            {working && st.mode !== 'working_fabric' && steps.azure_capacities && steps.azure_capacities.detail && steps.azure_capacities.state === 'ok' && <P>{steps.azure_capacities.detail}</P>}
            {!working && hasAzure && (
              <P>Your Azure connection is set up, so there is nothing to change in Fabric. The daily sync may not have read Fabric yet: check again, or read it now.</P>
            )}
            {!working && !hasAzure && (<>
              <P>
                The quickest way is your Azure connection. Its setup script creates one read-only app and assigns it the <strong>Reader</strong> role on
                each subscription you choose (on every subscription it can see if you choose none); Govern360 reads the subscriptions you list on the
                connection. Reader is enough for Govern360 to list your Fabric
                capacities and to read the dollars Azure bills for Fabric AI (the Copilot and AI meters in Azure Cost Management). Nothing is set up in Fabric,
                and no group or extra role is needed.
              </P>
              {canManage && <button type="button" className="btn primary" data-testid="fb-setup-connect-azure" style={{ height: 30, fontSize: 12.5, marginTop: 8 }}
                onClick={() => { if (onClose) onClose(); window.location.hash = CONNECT_AZURE_HASH; }}>Connect Azure<ExternalLink size={12} /></button>}
              <P>Already connected Microsoft? Govern360 can also read Fabric through that app once Fabric lets it call its APIs - see the checklist below.</P>
            </>)}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {canManage && <button type="button" className={'btn ' + (!working && hasAzure ? 'primary' : 'outline')} data-testid="fb-setup-check" style={{ height: 28, fontSize: 12 }} disabled={!!busy} onClick={check}><RefreshCw size={12} />{busy === 'check' ? ' Checking...' : ' Check again'}</button>}
              {canManage && <button type="button" className="btn outline" data-testid="fb-setup-sync" style={{ height: 28, fontSize: 12 }} disabled={!!busy} onClick={sync}>{busy === 'sync' ? 'Reading...' : 'Sync now'}</button>}
              {!canManage && <span style={{ fontSize: 12, color: 'var(--g-text-2)' }}>An owner or admin can run the check, the sync and the setup.</span>}
            </div>
            {msg && <div data-testid="fb-setup-msg" style={{ fontSize: 12, marginTop: 8, color: msg.ok ? 'var(--g-success)' : 'var(--g-warning)' }}>{msg.text}</div>}
          </section>

          {/* G360_K637 - either path is enough */}
          <div style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 6px' }}>What Govern360 checked</div>
          {PATHS.map((path) => (
            <div key={path.key} data-testid={'fb-path-' + path.key} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>{path.title} <span style={{ fontWeight: 400, color: 'var(--g-text-3)' }}>{path.sub}</span></div>
              <ol style={{ listStyle: 'none', padding: 0, margin: '6px 0 0' }}>
                {path.steps.map((k) => stepItem(k, SHOWN_ORDER.indexOf(k)))}
              </ol>
            </div>
          ))}

          {/* G360_K637 - optional extras: AI items, capacity role, events */}
          <section ref={extrasRef} data-testid="fb-setup-extras" style={{ border: '1px solid var(--g-divider)', borderRadius: 8, padding: '10px 12px', margin: '6px 0 12px' }}>
            <strong style={{ fontSize: 13.5 }}>Optional: add AI items (data agents, ontologies, operations agents)</strong>
            <P>
              Capacities and costs do not need this. To list the AI items on each capacity, Govern360 needs ONE Fabric admin setting:
              <strong> Service principals can access read-only admin APIs</strong>, turned on for a security group that contains the existing
              <strong> Govern360 (read-only)</strong> app. It is a Microsoft Fabric tenant setting, not an app permission, so it cannot be granted
              by admin consent - a Fabric administrator turns it on.
            </P>
            <div className="banner info" data-testid="fb-setup-pbi" style={{ background: 'var(--g-bg-alt)', color: 'var(--g-text-2)', marginTop: 8, fontSize: 12, lineHeight: 1.55 }}>
              Govern360 deliberately does not add Power BI permissions to its app. Microsoft's read-only admin APIs fail for an app that has admin-consented Power BI application permissions, so if yours has any, remove them.
            </div>
            <ol style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
              {EXTRA_STEPS.map((k) => stepItem(k, SHOWN_ORDER.indexOf(k)))}
            </ol>
            {/* G360_K636 - ONE script instead of a copy-paste list */}
            <section ref={scriptRef} data-testid="fb-setup-script" style={{ border: '1px solid var(--g-divider)', borderLeft: '3px solid #1a73e8', borderRadius: 8, padding: '10px 12px', marginTop: 4 }}>
              <strong style={{ fontSize: 13.5 }}>Set it up with one script</strong>
              <P>
                A Microsoft administrator runs this once in Azure Cloud Shell (PowerShell). It runs under <strong>your own sign-in</strong> -
                the Govern360 app never grants itself anything. It shows what it will do, asks Y/N, is safe to run again, never disables a
                setting or removes anyone, and prints a result for each step. If your sign-in lacks the rights for a step, it prints the
                manual step and carries on.
              </P>
              <ol data-testid="fb-setup-script-steps" style={{ fontSize: 12.5, color: 'var(--g-text-2)', lineHeight: 1.6, margin: '6px 0', paddingLeft: 20 }}>
                <li>Creates (or reuses) the security group <strong>{GROUP}</strong> and adds the Govern360 app to it.</li>
                <li>Turns on the Fabric tenant settings <strong>Service principals can call Fabric public APIs</strong> and <strong>Service principals can access read-only admin APIs</strong> for that group, keeping any groups already there (Microsoft Preview API; needs the Fabric administrator role).</li>
                <li>Lists your Fabric capacities and prints where to add the group as Contributor on each (by hand - Govern360 never changes capacity administrators). Optional.</li>
                <li>Gives your Govern360 Azure connection app <strong>Cost Management Reader</strong> on the subscriptions that hold Fabric capacities, if not already assigned (not needed when it already has Reader there).</li>
                <li>Tells you to come back here and click <strong>Check again</strong>.</li>
              </ol>
              <P>
                Filled in: Govern360 app (client) ID <span className="mono" data-testid="fb-script-cid">{ids && ids.clientId ? ids.clientId : 'not known yet - the script asks'}</span>
                {', '}Entra tenant <span className="mono" data-testid="fb-script-tid">{ids && ids.entraTenantId ? ids.entraTenantId : 'not known yet - the script asks'}</span>
                {', '}Azure connection app <span className="mono" data-testid="fb-script-az">{azId || 'not found - the script asks you to paste it (or skip)'}</span>.
              </P>
              {!scriptHasIds && <P><span data-testid="fb-script-noids" style={{ color: 'var(--g-warning)' }}>Microsoft is not connected yet, so the script will ask for the app and tenant IDs. Connect Microsoft first for a ready-to-run script.</span></P>}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0 2px' }}>
                <button type="button" className="btn outline" data-testid="fb-script-download" style={{ height: 26, fontSize: 12 }} onClick={downloadScript}><Download size={12} /> Download {FABRIC_SCRIPT_FILE}</button>
                <a className="btn outline" data-testid="fb-script-shell" style={{ height: 26, fontSize: 12 }} href="https://shell.azure.com/powershell" target="_blank" rel="noopener noreferrer">Open Azure Cloud Shell<ExternalLink size={12} /></a>
              </div>
              <P>Paste the whole script into Cloud Shell (PowerShell) and press Enter, or upload the downloaded file and run it.</P>
              <Snip testid="fb-script" text={script} maxHeight={260} />
            </section>
            {/* G360_K636 */}
            <details data-testid="fb-setup-manual" style={{ border: '1px solid var(--g-divider)', borderRadius: 8, padding: '8px 12px', marginTop: 10 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Do it by hand instead</summary>
              {MANUAL_KEYS.map((k) => (
                <div key={k} data-testid={'fb-manual-' + k} style={{ marginTop: 10 }}>
                  <strong style={{ fontSize: 12.5 }}>{STEP_TITLE[k]}</strong>
                  {body(k)}
                </div>
              ))}
            </details>
          </section>
        </div>
      )}
    </Drawer>
  );
}
